import { forwardRef, useEffect, useState, type ChangeEvent, type InputHTMLAttributes, type SelectHTMLAttributes, type TextareaHTMLAttributes } from 'react';

type Size = 'm' | 's';

export const TextInput = forwardRef<HTMLInputElement, Omit<InputHTMLAttributes<HTMLInputElement>, 'size'> & { size?: Size }>(function TextInput({ className = '', size, ...rest }, ref) {
  return <input ref={ref} type="text" className={`input ${size === 's' ? 'input-s' : ''} ${className}`} {...rest} />;
});

export const TextArea = forwardRef<HTMLTextAreaElement, TextareaHTMLAttributes<HTMLTextAreaElement>>(function TextArea({ className = '', ...rest }, ref) {
  return <textarea ref={ref} className={`textarea ${className}`} {...rest} />;
});

export const Select = forwardRef<HTMLSelectElement, Omit<SelectHTMLAttributes<HTMLSelectElement>, 'size'> & { size?: Size }>(function Select({ className = '', size, children, ...rest }, ref) {
  return (
    <select ref={ref} className={`select ${size === 's' ? 'input-s' : ''} ${className}`} {...rest}>
      {children}
    </select>
  );
});

export interface NumberInputProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'value' | 'onChange' | 'size'> {
  value: number | string | null | undefined;
  /** 'int' and 'float' commit numbers; 'long' keeps the digits as a string so values beyond 2^53 stay exact */
  kind?: 'int' | 'float' | 'long';
  onCommit: (value: number | string | null) => void;
  size?: Size;
  allowEmpty?: boolean;
}

/**
 * A number field that commits on blur or Enter (one coalesced change per edit, not per keystroke) and refuses text that
 * is not a number of its kind: the previous value comes back on Escape or an invalid blur.
 */
export function NumberInput({ value, kind = 'int', onCommit, size, allowEmpty = false, className = '', onKeyDown, ...rest }: NumberInputProps) {
  const toText = (v: number | string | null | undefined) => (v === null || v === undefined ? '' : String(v));
  const [text, setText] = useState(toText(value));
  const [invalid, setInvalid] = useState(false);
  useEffect(() => {
    setText(toText(value));
    setInvalid(false);
  }, [value]);

  const parse = (t: string): { ok: boolean; value: number | string | null } => {
    const s = t.trim();
    if (s === '') return allowEmpty ? { ok: true, value: null } : { ok: false, value: null };
    if (kind === 'long') return /^-?\d+$/.test(s) ? { ok: true, value: s.replace(/^-0+(?=\d)/, '-').replace(/^0+(?=\d)/, '') } : { ok: false, value: null };
    if (kind === 'int') return /^-?\d+$/.test(s) && Number.isSafeInteger(Number(s)) ? { ok: true, value: Number(s) } : { ok: false, value: null };
    const n = Number(s);
    return Number.isFinite(n) ? { ok: true, value: n } : { ok: false, value: null };
  };

  const commit = () => {
    const parsed = parse(text);
    if (!parsed.ok) {
      setInvalid(true);
      setText(toText(value));
      return;
    }
    setInvalid(false);
    if (toText(parsed.value) !== toText(value)) onCommit(parsed.value);
  };

  return (
    <input
      type="text"
      inputMode={kind === 'float' ? 'decimal' : 'numeric'}
      className={`input ${size === 's' ? 'input-s' : ''} ${className}`}
      value={text}
      aria-invalid={invalid || undefined}
      onChange={(e: ChangeEvent<HTMLInputElement>) => {
        setText(e.target.value);
        setInvalid(false);
      }}
      onBlur={commit}
      onKeyDown={(e) => {
        if (e.key === 'Enter') {
          e.preventDefault();
          commit();
        } else if (e.key === 'Escape') {
          setText(toText(value));
          setInvalid(false);
        }
        onKeyDown?.(e);
      }}
      {...rest}
    />
  );
}

export interface CommitTextInputProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'value' | 'onChange' | 'size'> {
  value: string;
  onCommit: (value: string) => void;
  /** a regex the value must match on commit (R-CM-02: validated when saved, never compiled per keystroke) */
  regex?: string | null;
  size?: Size;
  multiline?: boolean;
}

/** A text field that commits on blur or Enter (Escape restores), so one edit is one change in the log (Q-D-7). */
export function CommitTextInput({ value, onCommit, regex, size, multiline, className = '', onKeyDown, ...rest }: CommitTextInputProps) {
  const [text, setText] = useState(value);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    setText(value);
    setError(null);
  }, [value]);
  const commit = () => {
    if (text === value) return;
    if (regex) {
      let ok = true;
      try {
        ok = new RegExp(regex).test(text);
      } catch {
        ok = true; // an unparsable pattern never blocks the user; the server validates the template's regex
      }
      if (!ok) {
        setError(`Must match ${regex}`);
        return;
      }
    }
    setError(null);
    onCommit(text);
  };
  const common = {
    className: `${multiline ? 'textarea' : 'input'} ${size === 's' ? 'input-s' : ''} ${className}`,
    value: text,
    'aria-invalid': error ? true : undefined,
    title: error ?? undefined,
    onBlur: commit,
  };
  if (multiline) {
    return (
      <textarea
        {...(common as TextareaHTMLAttributes<HTMLTextAreaElement>)}
        onChange={(e) => setText(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Escape') setText(value);
          if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) commit();
        }}
        rows={(rest as { rows?: number }).rows ?? 3}
        placeholder={rest.placeholder}
        disabled={rest.disabled}
        id={rest.id}
        aria-label={(rest as { 'aria-label'?: string })['aria-label']}
      />
    );
  }
  return (
    <input
      type="text"
      {...common}
      onChange={(e) => setText(e.target.value)}
      onKeyDown={(e) => {
        if (e.key === 'Enter') {
          e.preventDefault();
          commit();
        } else if (e.key === 'Escape') {
          setText(value);
          setError(null);
        }
        onKeyDown?.(e);
      }}
      {...rest}
    />
  );
}

export function Checkbox({ label, className = '', ...rest }: InputHTMLAttributes<HTMLInputElement> & { label?: string }) {
  return (
    <label className={`check ${className}`}>
      <input type="checkbox" {...rest} />
      {label && <span>{label}</span>}
    </label>
  );
}

export function Switch({ label, checked, onChange, disabled, id, 'aria-label': ariaLabel }: { label?: string; checked: boolean; onChange: (v: boolean) => void; disabled?: boolean; id?: string; 'aria-label'?: string }) {
  return (
    <label className="switch">
      <input type="checkbox" role="switch" id={id} aria-label={ariaLabel ?? label} checked={checked} disabled={disabled} onChange={(e) => onChange(e.target.checked)} />
      <span className="switch-track" aria-hidden="true" />
      {label && <span>{label}</span>}
    </label>
  );
}
